import {
  getItemSku,
  type IListItem,
  type IProductBundleOption,
  type IProductBundleOptionValue,
  getBundleVariationImageCloudinaryPath,
} from '@rogue/universal'
import { reactive } from 'vue'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useProductStore } from '@/js/stores/productStore'
import stockStatus from '@/js/helpers/stockStatus'
import { computePriceSync } from '@/js/helpers/price-utils'
import { RACKBUILDER_BUNDLES, VISIBLE } from '@/js/stores/productConstants'
import { findEnabledCustomProductVariantBySku } from '@/js/customProducts/registry'
import type { OosItem } from '@/js/stores/listTypes'
import { BuyRequest, BuyRequestFlags, BuyRequestMeta, IRogue, Product } from '@/js/services/RogueTypes'

export function getSimpleForBuyRequest(buyRequest: Pick<IListItem, 'productId' | 'children'>, rogue: IRogue): Product {
  const productStore = useProductStore(rogue.pinia)
  let br = buyRequest
  let product = productStore.products[br.productId]
  while (br.children?.length) {
    br = br.children[0]
    product = productStore.products[br.productId]
  }

  return product
}

/**
 * Resolve the categoryId a buy request's category should be looked up under - mirrors the
 * linkId resolution in getBuyRequestMeta(). For a "grouped" family (kettlebells, ropes, etc.)
 * buyRequest.productId is the child simple that was actually added, not the visible/group
 * product - its category lives on the group product referenced by metadata.groupedProductId.
 */
export function getCategoryIdForBuyRequest(buyRequest: Pick<BuyRequest, 'productId' | 'metadata'>, rogue: IRogue): number | undefined {
  const product = rogue.getProductById(buyRequest.productId)

  if (product?.visibility === VISIBLE) {
    return product.primaryCategoryId
  }

  const groupedProductId = buyRequest.metadata?.groupedProductId

  return groupedProductId !== undefined ? rogue.getProductById(groupedProductId)?.primaryCategoryId : undefined
}

export function getCartThumbForFXBundle(rogue: IRogue, bundleBuyRequest: BuyRequest, bundleConfig:number[]) {
  const rack = rogue.getProductById(bundleConfig[0])
  const trainer = bundleConfig.map(id => rogue.getProductById(id)).find(p => p !== rack && p?.name.includes('Trainer'))!
  const bundle = rogue.getProductById(bundleBuyRequest.productId)

  if (!rack || !trainer || !bundle) {
    return ''
  }

  let edition = 'plate loaded'

  if (bundle.sku === 'RM-RHINO-RACK-BUNDLE') {
    edition = trainer.attributes.rig_type as string
  } else if (trainer.name.toLowerCase().indexOf('twin') !== -1) {
    edition = 'twin stack'
  } else if ((trainer.attributes.edition as string)?.toLowerCase().indexOf('stack') !== -1) {
    edition = 'weight stack'
  }

  const parts = [bundle.sku, ((rack.attributes.rig_color as string) || '').replace(' / ', '_')]

  if (bundle.sku === 'RM-RHINO-RACK-BUNDLE') {
    parts.push(rack.attributes.rack_size?.toString() || '')
  } else {
    parts.push(trainer.attributes.generic_size?.toString() || '')
  }

  parts.push(edition)

  const shrouds = trainer.attributes.rig_or_wm

  if (shrouds === 'No') {
    parts.push('No shrouds')
  }

  return `/bundle/${bundle.sku}/${parts.join('-').replace(/ /g, '_')}.png`.toLowerCase()
}

/**
 * Turn a bundle option into the default buyRequest for that bundle row.
 * Throws an error if this option is required and all values are OOS
 */

function getBundleOptionBuyRequest(opt: IProductBundleOption, parent:BuyRequest, rogue: IRogue, selectedProductId?: number): BuyRequest {
  const metadata: BuyRequest['metadata'] = {
    bundleOptionId   : opt.id,
    groupedProductId : parent.metadata?.groupedProductId,
  }

  const childRequest: BuyRequest = {
    productId : 0,
    qty       : 1,
    metadata,
    children  : [],
  }

  let isRequired = opt.isRequired

  let selectedVal: IProductBundleOptionValue | undefined

  // If the free product with this option is OOS, then all of the values are OOS
  if (opt.includedProductId && stockStatus.forProduct(rogue.getProductById(opt.includedProductId)) !== '') {
    selectedVal = undefined
  } else if (selectedProductId) {
    // See if the selected value was a simple (where the productId is directly available as a bundle option
    selectedVal = opt.values.find(val => val.productId === selectedProductId)
    if (!selectedVal) {
      // If the selectedVal was not available directly on the bundle, it might be a child of one of the bundle options...
      selectedVal = opt.values.find(val => rogue.getProductById(val.productId)?.childrenIds?.includes(selectedProductId as number))
    }
  } else {
    // More hardcoding for the rack bundles... @todo this should be a lot more dynamic, maybe use the bundle-utils file
    if (opt.title.includes('Front Pull')) {
      const bundle = rogue.getProductById(parent.productId)
      if (bundle && bundle.sku === 'RM-RHINO-RACK-BUNDLE') {
        const rack = getSimpleForBuyRequest(parent, rogue)

        if ((rack?.attributes.rack_size || '').toString().indexOf('6') !== -1) {
          isRequired = true
        }
      }
    }

    selectedVal = opt.values.find(val => (val as unknown as {isDefault: boolean}).isDefault)

    if (!selectedVal) {
      // Find the first bundle option that's in stock
      selectedVal = opt.values.find(val => stockStatus.forProduct(rogue.getProductById(val.productId)) === '')
    }
  }

  if (!selectedVal && isRequired) {
    selectedVal = opt.values[0]
    childRequest.productId = selectedVal.productId
    childRequest.qty = 0
  } else if (selectedVal) {
    childRequest.qty = (isRequired || selectedProductId || selectedVal.isDefault) ? selectedVal.defaultQty : 0
    childRequest.productId = selectedVal.productId
  } else if (!isRequired) {
    // Just this bundle opt is OOS - the parent can still be bought. Setting qty = 0 will select the "None" option.
    childRequest.qty = 0
    childRequest.productId = opt.values[0].productId
  } else {
    // throw new Error(`Required bundle option ${opt.title} ${opt.id} with no available options`)
    rogue.log.warn(`Tried to create a required bundle option for ${opt.title} but none of the options were available`)
    childRequest.qty = 0
    childRequest.productId = opt.values[0].productId
  }

  const configProduct = rogue.getProductById(childRequest.productId)

  if (!configProduct) {
    rogue.log.warn(`Could not find configurable product ${childRequest.productId} for bundle option ${opt.title}`)
  }
  if (configProduct?.type === 'configurable' && configProduct.childrenIds) {
    selectedProductId = selectedProductId || Number(configProduct.attributes.rogue_configurable_default) || configProduct.childrenIds[0]

    if (selectedProductId && !configProduct.childrenIds.includes(selectedProductId)) {
      selectedProductId = configProduct.childrenIds[0]
    }
    const simple = rogue.getProductById(selectedProductId)

    childRequest.children?.push(createBuyRequest(simple, rogue))
  }

  return reactive(childRequest)
}

export function recreateBundleBuyRequest(buyRequest: BuyRequest, rogue: IRogue, bundleSelections: number[]) {
  let bundle = buyRequest

  while (bundle && bundle.type !== 'bundle' && bundle.children) {
    bundle = bundle.children[0]
  }

  const addons = bundle.children

  if (!bundle || !addons) {
    rogue.log.warn('Invalid bundle buy request')

    return
  }

  const bundleProduct = rogue.getProductById(bundle.productId)

  bundleSelections.forEach((productId, indx) => {
    const option = bundleProduct.bundleOptions?.[indx]
    if (!option) {      
      return
    }

    // Choose the default bundle option for required bundle options from Quote Tool links that have incomplete bundle options
    if (!productId && option.isRequired) {
      return
    }
    // The bundle part will contain the productId that they selected
    else if (!productId) {
      addons[indx].qty = 0
    } else {

      try {
        const newBuyRequest = getBundleOptionBuyRequest(option, bundle, rogue, productId)
        addons[indx].productId = newBuyRequest.productId
        addons[indx].qty = newBuyRequest.qty
        if (addons[indx].children?.length !== newBuyRequest.children?.length) {
          addons[indx].children = newBuyRequest.children
        } else if (addons[indx].children?.[0]) {
          addons[indx].children![0].productId = newBuyRequest?.children![0].productId
        }
        // addons[indx].children =

        const freeProductId = option.includedProductId
        if (freeProductId && !bundle.children?.some(child => child.productId === freeProductId)) {
          // This isn't ideal to make a buy request inline like this since it will only work with simple buy requests.
          // But the free product id isn't loaded at this point in SSR, so if we call createBuyRequest it may fail
          bundle.children?.push(reactive({
            productId : freeProductId,
            qty       : 1,
            metadata  : { freeBundleOptionId: option.id },
          }))
        }
      } catch (e) {
        rogue.log.warn(`bundle option ${option.title} cannot select ${productId}`, { opts: option.values.map(v => rogue.getProductById(v.productId)) })
      }
    }
  })
}

/**
 * Checks whether `selectedChild` is `child.productId` itself, or lives anywhere in its subtree
 * (e.g. a simple nested two levels down inside a bundle option) - not just its direct children.
 */
function containsSelectedChild(child: Pick<BuyRequest, 'productId' | 'children'>, selectedChild: number): boolean {
  return child.productId === selectedChild || (child.children ?? []).some(c => containsSelectedChild(c, selectedChild))
}

/**
 * Create a buyRequest object
 * @param product the product to create the request
 * @param rogue
 * and we want to check if the product should actually be
 * @param selectedChild If we want to preselect a certain child
 * @param selectedOptions If we want to preselect certain custom options
 */
export function createBuyRequest(product: Product, rogue: IRogue, selectedChild?: number, selectedOptions?: { [key: string]: number }): BuyRequest {
  const productStore = useProductStore(rogue.pinia)
  const products = productStore.products

  if (!product) {
    throw new Error('Product not found to create buy request')
  }

  // Set up Giftcard metadata structure
  if (product.giftcardData?.type === 'Virtual') {
    const { amounts } = product.giftcardData

    return {
      productId  : product.productId,
      qty        : 1,
      isGiftcard : true,
      metadata   : {
        giftcard: {
          delivery       : 'email',
          amount         : amounts[0],
          customAmount   : undefined,
          message        : '',
          recipientEmail : '',
          recipientName  : '',
          senderEmail    : '',
          senderName     : '',
          design         : '/UI/zeus/gift-cards/email-images/Product_Image_11_g4lhzx.png',
        },
      },
    }
  }

  if (product.hasRegistration) {
    return reactive({
      type      : 'group',
      isEvent   : true,
      productId : product.productId,
      qty       : 1,
      children  : [],
    })
  }

  const buyRequest: BuyRequest = {
    productId : product.productId,
    qty       : product?.inventory?.min || 1,
  }

  // If selectedChild is a part of the parent product
  const canUseSelectedChild = selectedChild && (product.childrenIds || []).includes(selectedChild) && product.type !== 'grouped'
  // A product response might have invalid product ids in it's childrenIds array.
  // We need to filter these out so we don't throw a runtime exception
  const validChildren = (product.childrenIds || [])
    .filter(childId => (!canUseSelectedChild || childId === selectedChild) && products[childId])
    .map(childId => products[childId])

  if (product.type === 'grouped') {
    // Grouped products get one child buy request for each child. Then it can have qty set to 0
    buyRequest.children = []
    buyRequest.type = 'group'

    if (validChildren.length === 1 && !product.roguebuilder?.props?.showAsGrouped) {
      // Still pass in the selectedChild even if it wasn't a part of the the current products children. For grouped products it may be the case that we have three product levels, where the selected child is a part of the last level. Grouped -> Config -> Simple
      const retval = createBuyRequest(validChildren[0], rogue, selectedChild, selectedOptions)

      retval.metadata = retval.metadata || {}
      retval.metadata.groupedProductId = product.productId

      return retval
    }

    // Find our Primary child (which has qty = 1 to start). The rest are "accessories"
    let main = validChildren[0]

    // "Swatched" grouped products show up like configurables - they just have one child set to the selected simple.
    if (product.rhpaDisplayType === 'swatch') {
      main = validChildren.find(child => stockStatus.forProduct(child) === '') || validChildren[0]
      if (selectedChild) {
        main = validChildren.find(child => child.productId === selectedChild) || main
      }
      const child = createBuyRequest(main, rogue, undefined, selectedOptions)
      child.metadata = { groupedProductId: buyRequest.productId }
      child.qty = 1
      buyRequest.children = [child]
    } else {
      // Normal grouped products track one buy request per child - since you can add quantity to each separately.
      // When they are added to the cart, they actually separate into multiple items
      buyRequest.children = validChildren.map(child => createBuyRequest(child, rogue, selectedChild, selectedOptions))
      buyRequest.children.forEach((child, i) => {
        child.metadata = { groupedProductId: buyRequest.productId }
        if (selectedChild) {
          // If this is a grouped product. Search the whole subtree, not just direct children -
          // a bundle option can itself have a nested child (bundle -> bundle option -> simple).
          child.qty = containsSelectedChild(child, selectedChild) ? 1 : 0
        } else {
          // Only set qty for the main item if it's in stock. All accessories start at 0.
          child.qty = (i === 0 && !product.attributes?.rogue_children_qty_zero) && stockStatus.forBuyRequest(child, products) === '' ? child.qty : 0
        }
      })

      if (buyRequest.children.length === 0) {
        rogue.log.error('No valid children for grouped product to create a buy request', `Product: ${product.sku}`)
      }
    }
    buyRequest.qty = 1
  }

  if (product.type === 'bundle') {
    buyRequest.type = 'bundle'

    buyRequest.children = []

    product.bundleOptions?.forEach(opt => {
      try {
        buyRequest.children?.push(getBundleOptionBuyRequest(opt, buyRequest, rogue))
      } catch (e) {
        rogue.log.warn(`Error creating bundle option ${opt.title}: ${JSON.stringify(opt.values)}`, e as Error)
      }
    })
  }

  if (product.type === 'configurable') {
    if (validChildren.length === 0) {
      rogue.log.error('No valid children for configurable to create a buy request', `Product: ${product.sku}`)
    } else {
      const defaultId = product.attributes.rogue_configurable_default

      // We need to pick a default simple. This is based on stock status and rogue_configurable_default
      // @todo This should be chosen based on customer's latest selected options (if they pick shirt size: medium, reselect that option if possible)
      const options = validChildren.map(childProduct => {
        let order = stockStatus.forProduct(childProduct) !== '' ? 100 : 0

        if (`${childProduct.productId}` !== `${defaultId}` && childProduct.sku !== defaultId) {
          order += 10
        }

        return { childProduct, order }
      })

      // If the rogue_configurable_default is in stock, use that - otherwise use the first in-stock option
      options.sort((a, b) => a.order - b.order)

      buyRequest.children = [createBuyRequest(options[0].childProduct, rogue, selectedChild, selectedOptions)]
      if (['GIFTCARD', 'GIFTCARDS'].includes(product.sku)) {
        buyRequest.isPhysicalGiftcard = true
      }
    }
  }

  // Set custom options to the defaults
  if (product.customOptions) {
    const options: Record<number, any> = {}
    product.customOptions.forEach(opt => {
      // If we are picking a specific child (usually via the feed) we want to pick the FREE option instead of the first
      if (selectedChild && !selectedOptions?.[opt.id] && opt.type !== 'area') {
        options[opt.id] = opt.values?.slice(0).sort((a, b) => a.price - b.price)[0]?.id || null
      } else if (opt.type === 'area') {
        options[opt.id] = ' '
      } else {
        options[opt.id] = selectedOptions?.[opt.id] || opt.values?.[0]?.id || null
      }
    })

    buyRequest.options = options
  }

  return reactive(buyRequest)
}

/**
 * If we only have a simpleId or a SKU, we may need to load parent products in order to create a valid buy request
 * For example, if we only have the productId for "Basic Shirt Green size Medium", we need to find the parent product first
 * before we can create the real buy request
 *
 * NOTE - does not handle simples where the only parent is a bundle, or where custom options are required.
 * If necessary, see code in m2_gyms microservice - which can do both
 *
 * @param skuOrId - the SKU or id of the product
 * @param rogue
 * @param qty
 * @param selectedChild - used for recursion
 * @param selectedOptions - used for recursion
 */
export async function createBuyRequestAsync(skuOrId: string | number, rogue: IRogue, qty = 1, selectedChild?: number, selectedOptions?: BuyRequest['options']): Promise<BuyRequest> {
  const productStore = useProductStore(rogue.pinia)
  const product: Product = await productStore.load(skuOrId, rogue)
  if (!product) {
    throw new Error(`Couldnt find product ${skuOrId}`)
  }
  let buyRequest = createBuyRequest(product, rogue, selectedChild, selectedOptions)
  buyRequest.qty = qty

  // If the product is visible on its own (visibility === 4) then we dont need to load the parents
  if (product.visibility !== 4) {
    // Use the product store to load the first available parent
    const parent: Product | undefined = await productStore.findParent(product.productId, rogue)

    // If we couldnt find a parent, just return the original buy request
    if (parent) {
      if (parent.type === 'grouped') {
        // Grouped products are "dropped" when you add to the cart - but we do need the ID in metadata for linking back to the PDP
        buyRequest.metadata = { groupedProductId: parent.productId }
      } else if (parent.type === 'configurable') {
        // Configurable parents can use the "SelectedChild"
        buyRequest = await createBuyRequestAsync(parent.productId, rogue, qty, product.productId, selectedOptions)
      }
    }
  }

  return buyRequest
}

/**
 * Get a bunch of metadata (like Product Title, simple sku, configuration, etc) about a buy request into a single object.
 * This is a recursive function.
 */
export function getBuyRequestMeta(buyRequest: BuyRequest, rogue: IRogue, parent?: BuyRequest): BuyRequestMeta {
  // Get the main product for this buy request
  const product = rogue.getProductById(buyRequest.productId)

  const flags: BuyRequestFlags = {}
  const retval: Partial<BuyRequestMeta> = { flags: {}, categories: [] }
  // Custom RHPA lines (Cave, MS/MMS, Slinger X, Hybrid) restore from ?rid= on their group's PDP, which ignores the bundle child ids ?b= carries.
  const isCustomProductLine = !!product && !!findEnabledCustomProductVariantBySku(rogue, product.sku)

  // Reactive watchers call this, so an unloaded or bogus productId would otherwise throw
  // somewhere unrelated to whatever put it on the buy request
  if (!product) {
    rogue.log.warn('No product for buy request', buyRequest.productId)

    return retval as BuyRequestMeta
  }

  retval.name = product.name
  retval.type = product.type
  retval.feedId = product.feedId

  if (product.type === 'simple') {
    if (product.attributes.custom_stock_status) {
      retval.customStockStatus = product.attributes.custom_stock_status
    }
  }

  if (product.type === 'grouped' || product.type === 'simple') {
    // Really these should only be defined on the simples, but sometimes we set on the group to add a badge to RHPA...
    const { free_3: free3, cart_free:cartFree, free_shipping: free, shipperhq_shipping_group: shippingGroups } = product.attributes

    // 3 Free Shipping Group is added to products that are displayed for APO, but 3 Ships Free is not available for APO. Only use shipping groups if not APO
    if (free3 !== undefined || (shippingGroups?.some(g => ['3_FREE_ALL'].includes(g)) && rogue.site.code !== 'APO')) {
      flags.free3 = true
    }
    if (shippingGroups?.some(g => ['FREE', 'EU Zone 1 Free Shipping', 'EU Zone 1 Free Shipping - Heavy', 'Rack_Free'].includes(g)) || free) {
      flags.freeShip = true
    }
    if (cartFree) {
      flags.freeCartShip = true
    }
  }

  if (product.visibility === 4) {
    retval.link = product.url
    retval.linkId = product.productId
    retval.linkSku = product.sku
    retval.linkFeedId = product.feedId
  }

  if (product.thumbnail) {
    retval.thumbnail = product.thumbnail
  }

  const qty = buyRequest.qty * (parent?.qty || 1)

  if (product.type === 'grouped') {
    retval.groupId = product.productId
    retval.groupSku = product.sku
    // Find the first child with qty > 0 to use as the "main" product.
    if (product.hundoPricing) {
      flags.hundo = true
    }

    if (buyRequest.children?.length) {
      const children = buyRequest.children.map(child => getBuyRequestMeta(child, rogue, buyRequest))

      if (children.every(child => child.flags.fiveForFive)) {
        flags.fiveForFive = true
      }
      if (children.every(child => child.flags.free3)) {
        flags.free3 = true
      }
      if (children.every(child => child.flags.freeShip)) {
        flags.freeShip = true
      }
      if (children[0].leadTime && children.every(child => child.leadTime === children[0].leadTime)) {
        retval.leadTime = children[0].leadTime
      }

      if (children.some(child => child.flags.lightDirect)) {
        flags.lightDirect = true
      }

      if (children.some(child => child.flags.heavyDirect)) {
        flags.heavyDirect = true
      }

      if (children[0].customStockStatus && children.every(child => child.customStockStatus === children[0].customStockStatus)) {
        retval.customStockStatus = children[0].customStockStatus
      }
    }
  } else {
    /**
     * Not a grouped product
     */

    // This buy request might need thumbnail or link data from its grouped parent
    if (buyRequest.metadata?.groupedProductId) {
      const grouped = rogue.getProductById(buyRequest.metadata.groupedProductId)
      if (grouped) {
        retval.groupSku = grouped.sku
        retval.groupId = grouped.productId
        retval.thumbnail = retval.thumbnail || grouped.thumbnail
        retval.linkId = retval.linkId || grouped.productId
        retval.link = retval.link || grouped.url
        retval.linkSku = retval.linkSku || grouped.sku
        retval.linkFeedId = retval.linkFeedId || grouped.feedId
      }
    }

    // grab the single item price for the buyRequest
    if (product.type !== 'bundle') {
      try {
        const singleItem = { ...buyRequest }
        singleItem.qty = 1
        retval.basePrice = computePriceSync(singleItem, rogue.pinia!, { parent })
      } catch (e) {
        const err = e as Error
        rogue.log.warn('Unable to compute basePrice', {
          sku          : product.sku,
          errorName    : err.name,
          errorMessage : err.message,
          stack        : err.stack,
        })
      }
    }

    if (product.type === 'simple' || product.type === 'virtual') {
      retval.sku = getItemSku(buyRequest, rogue, rogue.site.defaultStoreId)
      retval.id = product.productId
      const { min, max, backorder, qoh, leadTime, restockStatus } = product.inventory
      retval.inventory = { min, max }
      retval.price = computePriceSync(buyRequest, rogue.pinia!, { parent })
      if (product.special_price) {
        retval.specialPrice = product.special_price

        if (product.price && retval.specialPrice < product.price) {
          retval.originalPrice = product.price
        }
      }

      if (product.hasPromo) {
        flags.hotDeal = true
        if (product.price) {
          retval.originalPrice = product.price
        }

        if (product.attributes.rogue_promo_price) {
          retval.specialPrice = Number(product.attributes.rogue_promo_price)
        }
      }

      if (max === 0 && restockStatus) {
        retval.leadTime = restockStatus
      } else if ((backorder && leadTime) || (qoh && leadTime && qty > qoh)) {
        retval.leadTime = leadTime
      }
      if (product.shipDirect) {
        if (rogue.site.heavyDirect?.some(grp => grp === product.shipDirect)) {
          flags.heavyDirect = true
        }
        if (rogue.site.directShip?.some(grp => grp === product.shipDirect)) {
          flags.lightDirect = true
        }
      }

      if (product.attributes.black_friday_dropship && rogue.featureFlags.isOn('fiveforfive_enabled')) {
        flags.fiveForFive = true
      }
    } else if (product.type === 'configurable') {
      // Save configurable data
      retval.configSku = product.sku
      retval.configId = product.productId

      const firstChild = buyRequest.children?.[0]

      if (firstChild) {
        Object.assign(retval, getBuyRequestMeta(firstChild, rogue, buyRequest))

        // Sometimes configurables have custom options that should be applied to the simple
        if (product.customOptions?.length) {
          const configSku = getItemSku(buyRequest, rogue, rogue.site.defaultStoreId)
          if (configSku !== retval.sku) {
            retval.baseSku = retval.sku
            retval.sku = configSku
          }
          retval.price = computePriceSync(buyRequest, rogue.pinia!)
        }

        if (retval.id) {
          const simple = rogue.getProductById(retval.id)
          const config: BuyRequestMeta['configuration'] = {}
          product.configurableAttributes?.forEach(attr => {
            config[attr.title] = { code: attr.code, value: simple.attributes[attr.code] }
          })
          retval.configuration = config
        }
      }
      retval.name = product.name
    } else if (product.type === 'bundle') {
      retval.bundleSku = product.sku

      const bundleOptions = product.bundleOptions?.map(opt => buyRequest.children?.find(child => child.metadata?.bundleOptionId === opt.id))

      if (!isCustomProductLine) {
        retval.link += `?b=${bundleOptions?.map(child => (!child ? 0 : getSimpleForBuyRequest(child, rogue)?.productId)).join('-')}`
      }

      const firstChild = buyRequest.children?.[0]
      if (firstChild) {
        Object.assign(retval, getBuyRequestMeta(firstChild, rogue, buyRequest))
        retval.feedId = product.feedId
      }

      // For functional trainer bundles, use the name of the bundle product instead of the default "Rogue FM-6 Functional Trainer"
      if (RACKBUILDER_BUNDLES.includes(product.sku.toUpperCase())) {
        retval.name = product.name
      }

      try {
        // the buyRequest is reset to the first child for bundle items, meaning we need to wait till that assignment happens to figure out the single item price
        const bundleSingle = { ...buyRequest }
        bundleSingle.qty = 1
        retval.basePrice = computePriceSync(bundleSingle, rogue.pinia!)
        retval.price = computePriceSync(buyRequest, rogue.pinia!)
      } catch (e) {
        //
      }

      const inventories = buyRequest.children?.map(child => getSimpleForBuyRequest(child, rogue))
        .map(prod => prod?.inventory || { min: 0, max: 99999 }) || []

      retval.inventory = {
        max : Math.min(...inventories.map(i => i.max)),
        min : Math.max(...inventories.map(i => i.min)),
      }
    } else if (product.type === 'giftcard') {
      retval.sku = product.sku
      retval.id = product.productId
      retval.price = buyRequest.metadata?.giftcard?.amount || product.price
      retval.thumbnail = buyRequest.metadata?.giftcard?.thumbnail || retval.thumbnail
    }
  }

  if (product.customOptions?.length) {
    retval.customOptions = retval.customOptions || {}
    product.customOptions.forEach(opt => {
      // @ts-ignore
      retval.customOptions[opt.title] = opt.values?.find(val => val.id === buyRequest.options?.[opt.id])?.title
    })
  }

  try {
    const productStore = useProductStore(rogue.pinia)
    retval.qty = buyRequest.qty
    retval.total = computePriceSync(buyRequest, rogue.pinia!)
    retval.stockStatus = stockStatus.forBuyRequest(buyRequest, productStore.products)
  } catch (e) {
    //
  }

  if (retval.linkId) {
    const visibleProduct = rogue.getProductById(retval.linkId)

    retval.brand = retval.brand || visibleProduct.attributes?.manufacturer
    // Load category data from the category store
    const categoryStore = useCategoryStore(rogue.pinia)
    const category = categoryStore.categories[visibleProduct.primaryCategoryId]
    if (category) {
    // Category path is an array of category names that we can use
      retval.categories = category.path.map((path:any) => path.name).concat(category.name)
    }
  }

  retval.linkPath = retval.link

  if (retval.link?.startsWith('/')) {
    retval.link = rogue.localizedBaseURL + retval.link
  }

  if (retval.thumbnail) {
    retval.thumbnail = getCloudinaryUrl(retval.thumbnail, 800)
  }

  if (buyRequest.metadata?.recipeId) {
    flags.zeus = true
    retval.recipeId = buyRequest.metadata.recipeId
    // Add the link to the SKU
    retval.link = `${retval.link}?rid=${retval.recipeId}`
    retval.linkPath = `${retval.linkPath}?rid=${retval.recipeId}`
    if (buyRequest.metadata?.nonFluidCustomization) {
      retval.thumbnail = getCloudinaryUrl(`/zeus/${retval.recipeId}/render.png`, 800, 800, null)
    }
  }

  // Same shape as the Zeus block above, keyed on the custom-product recipe rather than recipeId; guarded so an unloaded group never yields "undefined?rid=".
  if (isCustomProductLine && buyRequest.metadata?.recipe && retval.link) {
    retval.link = `${retval.link}?rid=${buyRequest.metadata.recipe}`
    retval.linkPath = `${retval.linkPath}?rid=${buyRequest.metadata.recipe}`
  }

  if (product.type === 'bundle' && product.attributes.plp_variation_thumbnail_pattern) {
    try {
      retval.thumbnail = getCloudinaryUrl(getBundleVariationImageCloudinaryPath(buyRequest, rogue), 800, 800, null)
    } catch (e) {
      rogue.log.warn(`Error creating bundle thumbnail for ${product.productId}`)
    }
  } else if (RACKBUILDER_BUNDLES.includes(product.sku.toUpperCase())) {
    // Keeping this so that it is possible to deploy dark the  getBundleVariationImageCloudinaryPath - but
    // these should all be converted into the new format eventually and this block can be deleted
    try {
      const bundleOpts = buyRequest.children?.map(child => (!child ? 0 : getSimpleForBuyRequest(child, rogue)?.productId)) || []
      const thumb = getCartThumbForFXBundle(rogue, buyRequest, bundleOpts)
      retval.thumbnail = getCloudinaryUrl(thumb)
    } catch (e) {
      rogue.log.warn('Error generating thumbnail for FX Trainer', e as Error)
    }
  }

  if (buyRequest.metadata?.registry) {
    retval.registryId = buyRequest.metadata.registry.registryId
  }

  if (product.type === 'bundle') {
    // The children need to point back to the parent
    // @todo this could be used by reading the `parent` property while creating the child
    const overwriteChildProps: Partial<BuyRequestMeta> = {
      link      : retval.link,
      bundleSku : product.sku,
      groupSku  : retval.groupSku,
      linkId    : retval.linkId,
      linkPath  : retval.linkPath,
      linkSku   : retval.linkSku,
    }
    try {
      retval.bundleProducts = buyRequest.children?.map((addon, i) => {
      /**
       * The main product was computed as zero price to the `computePriceSync` function
       * Since we want to know the price of the squat stand etc, dont send the "parent" which is the group
       */
        const parentBuyReq = i === 0 ? undefined : buyRequest

        // Get the meta for this specific buy request meat
        return Object.assign({}, getBuyRequestMeta(addon, rogue, parentBuyReq), overwriteChildProps)
      })
        .filter(child => child.qty > 0 && child.id !== retval.id)
    } catch (e) {
      rogue.log.warn('Tried to create buyRequestMeta for bundle before children are loaded')
    }
  }

  if (retval.flags) {
    Object.assign(retval.flags, flags)
  }

  return retval as BuyRequestMeta
}

/**
 * If a buy request is a bundle, we might have one order item that is really three or four separate products.
 * For analytics, we want to track each individual product, not just the parent bundle
 * @param buyRequests
 * @param rogue
 */
export function flatBuyRequestMetas(buyRequests: BuyRequest[], rogue: IRogue): BuyRequestMeta[] {
  return buyRequests.map(buyRequest => getBuyRequestMeta(buyRequest, rogue))
    .map(meta => (meta.bundleProducts?.length ? meta.bundleProducts : [meta]))
    .flat()
}

/**
 * Returns productIds from the list of buy request
 * @param {BuyRequest[]} buyRequests
 * @param {boolean} onlySimples If true, only return productIds for simple products that affect stock / shipping (vs display also)
 */
export function listProductIds(buyRequests: BuyRequest[], onlySimples: boolean = false): number[] {
  const retval: number[] = []

  buyRequests?.forEach(item => {
    retval.push(...listProductIds(item.children || []))
    if (!onlySimples || !item.children?.length) {
      retval.push(item.productId)
    }
    if (!onlySimples && item.metadata?.groupedProductId) {
      retval.push(item.metadata?.groupedProductId)
    }
  })

  return retval.filter((id, i) => !!id && retval.indexOf(id) === i)
}

export function getOosItems(items: BuyRequest[], products: Record<number, Product>, rogue: IRogue) {
  const retval: OosItem[] = []

  try {
    items.forEach((item: BuyRequest) => {
      const product = getSimpleForBuyRequest(item, rogue)
      // @ts-ignore
      const status = stockStatus.forBuyRequest(item, products)

      if (status === 'bin') {
        retval.push({ product, item, itemId: item._id || item.listItemId || '', type: 'bin' })
      } else if (status === 'oos') {
        retval.push({ product, item, itemId: item._id || item.listItemId || '', type: 'oos' })
      } else {
        const max = product.inventory.promoQty ? Math.min(product.inventory.max, product.inventory.promoQty!) : product.inventory.max

        if (item.qty > max) {
          retval.push({ product, item, itemId: item._id || item.listItemId || '', type: 'qty' })
        }
      }
    })
  } catch (e) {
    //
  }

  return retval
}

// Check if the product has been marked as altered since it was added to the cart
export function productHasChanged(item: BuyRequest, products: Record<number, Product>): boolean {
  if (!item.createdAt || !products[item.productId]?.attributes.options_updated_date) {
    return !!item.children?.some(child => productHasChanged(child, products))
  }

  const createdDate = new Date(item.createdAt).getTime()
  const options_updated_date = products[item.productId]?.attributes.options_updated_date as string
  // options_updated_date value is in GMT
  const updatedDate = new Date(options_updated_date?.replace(' ', 'T') + 'Z').getTime()
  const currentTime = new Date().getTime()

  return (currentTime > updatedDate && updatedDate > createdDate) || !!item.children?.some(child => productHasChanged(child, products))
}